/* eslint-disable @typescript-eslint/ban-types */

import React, { ReactElement, ReactNode } from 'react';
import styled from '@emotion/styled';

import { CustomPicker, ColorResult, InjectedColorProps } from 'react-color';
import { Saturation, Hue } from 'react-color/lib/components/common';
import { ColorWrapChangeHandler } from 'react-color/lib/components/common/ColorWrap';
import { MIXIN_SCREEN_READER_TEXT } from '../Style/mixins';
import { hexToColor } from './helpers';
import { COLOUR_PICKER_DEFAULT_PRESET_COLOURS } from './constants';
import { OL_COLOUR_BLACK_10 } from '../Style/colours';
import { Button } from '../Buttons/Button/Button';
import { TextField } from '../TextField/component';

// Swatch

const SwatchButton = styled.button(({ color }) => ({
  display: 'block',
  width: 16,
  height: 16,
  background: color,
  cursor: 'pointer',
  border: 'none',
  borderRadius: 3,
  outline: `${color} none medium`,
  boxShadow: 'rgba(0, 0, 0, 0.15) 0 0 0 1px inset',
  '&:focus': {
    boxShadow: `rgba(0, 0, 0, 0.15) 0 0 0 1px, rgba(0, 0, 0, 0.15) 0 8px 16px, ${color} 0 0 4px`,
  },
}));

const SwatchButtonLabel = styled.div(MIXIN_SCREEN_READER_TEXT);

declare interface SwatchInterface {
  color: string;
  onClick: (colorResult: ColorResult) => void;
  autoFocus: boolean;
}

const Swatch = ({
  color,
  onClick,
  autoFocus,
}: SwatchInterface): ReactElement => (
  <SwatchButton
    color={color}
    onClick={() => {
      onClick(hexToColor(color));
    }}
    autoFocus={autoFocus}
  >
    <SwatchButtonLabel>{color}</SwatchButtonLabel>
  </SwatchButton>
);

// CustomColourPickerComponent

const PickerContainer = styled.div({
  position: 'absolute',
  width: 200,
  padding: 10,
  background: '#fff',
  borderRadius: 4,
  boxShadow:
    'rgba(0, 0, 0, 0.15) 0px 0px 0px 1px, rgba(0, 0, 0, 0.15) 0px 8px 16px',
  zIndex: 3,
});

const SwatchList = styled.div({
  display: 'grid',
  width: '100%',
  gridGap: 5,
  gridTemplateColumns: 'repeat(auto-fill, minmax(16px, 1fr))',

  marginBottom: 10,
  paddingBottom: 10,
  borderBottom: `1px solid ${OL_COLOUR_BLACK_10}`,
});

const SwatchListItem = styled.div({
  display: 'block',
});

const SaturationContainer = styled.div({
  position: 'relative',
  height: 100,
  marginBottom: 10,
});

const HueContainer = styled.div({
  position: 'relative',
  height: 10,
  marginBottom: 10,
});

const ButtonsContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'row-reverse',
  marginTop: 10,
});

const TextFieldContainer = styled.div({
  '.ol-text-field': {
    display: 'block',
  },
});

declare interface MyColourPickerProps extends InjectedColorProps {
  onChange: ColorWrapChangeHandler;
  onChangeComplete: ColorWrapChangeHandler;
  onClose: () => void;
  presetColors?: string[] | null;
}

interface PickerState {
  textFieldValue: string;
  textFieldFocused: boolean;
}

class CustomColourPickerComponent extends React.Component<MyColourPickerProps> {
  public state: PickerState;

  public constructor(props: MyColourPickerProps) {
    super(props);

    const { hex } = props;

    this.state = {
      textFieldValue: hex || '',
      textFieldFocused: false,
    };
  }

  static getDerivedStateFromProps(
    nextProps: MyColourPickerProps,
    prevState: PickerState
  ): {} | null {
    const { textFieldFocused, textFieldValue } = prevState;

    // Only update the textfield when it's not being typed in
    // otherwise the value won't be updated
    if (nextProps.hex !== textFieldValue && !textFieldFocused) {
      return { textFieldValue: nextProps.hex };
    } else return null;
  }

  private handleTextFieldChange = (value: string): void => {
    const { onChange } = this.props;
    const colorHex = value;
    const color = hexToColor(colorHex);

    onChange(color);
  };

  private handleTextFieldOnFocus = (): void => {
    this.setState(() => ({ textFieldFocused: true }));
  };

  private handleTextFieldOnBlur = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.handleTextFieldChange(event.target.value);
    this.setState(() => ({ textFieldFocused: false }));
  };

  private handleHexFieldKeyUp = (
    event: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    // when 'enter' key pressed
    if (event.keyCode === 13) {
      event.preventDefault();
      this.handleTextFieldChange((event.target as HTMLInputElement).value);
    }
  };

  private handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    const { onClose } = this.props;

    // when 'esc' key pressed
    if (event.keyCode === 27) {
      event.preventDefault();
      onClose();
    }
  };

  public render(): ReactNode {
    const {
      onChangeComplete,
      onClose,
      presetColors = COLOUR_PICKER_DEFAULT_PRESET_COLOURS,
    } = this.props;

    const { textFieldValue } = this.state;

    return (
      <PickerContainer onKeyDown={this.handleKeyPress}>
        {presetColors && (
          <SwatchList>
            {presetColors.map((presentColor, index) => (
              <SwatchListItem key={`swatch${presentColor.substring(1)}`}>
                <Swatch
                  color={presentColor}
                  onClick={onChangeComplete}
                  autoFocus={index === 0}
                />
              </SwatchListItem>
            ))}
          </SwatchList>
        )}
        <SaturationContainer>
          <Saturation {...this.props} />
        </SaturationContainer>
        <HueContainer>
          <Hue {...this.props} direction="horizontal" />
        </HueContainer>
        <TextFieldContainer>
          <TextField
            label="Hex code"
            value={textFieldValue}
            onValueChange={(colorHex) => {
              this.setState(() => ({ textFieldValue: colorHex }));
            }}
            onFocus={this.handleTextFieldOnFocus}
            onBlur={this.handleTextFieldOnBlur}
            onKeyUp={this.handleHexFieldKeyUp}
            maxLength={7}
            focusOnMount={!presetColors}
          />
        </TextFieldContainer>

        <ButtonsContainer>
          <Button onClick={onClose} label="close" />
        </ButtonsContainer>
      </PickerContainer>
    );
  }
}

// eslint-disable-next-line new-cap
export const CustomColourPicker = CustomPicker(CustomColourPickerComponent);
